import AsyncStorage from '@react-native-async-storage/async-storage';

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

/**
 * Fetch Chess.com player stats
 * API Docs: https://www.chess.com/news/view/published-data-api
 */
export const fetchChesscomStats = async (username) => {
  if (!username) return null;

  const cacheKey = `chesscom_${username}`;
  
  try {
    // Check cache first
    const cached = await AsyncStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }

    // Fetch from API
    const response = await fetch(
      `https://api.chess.com/pub/player/${username}/stats`
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const rawData = await response.json();
    
    // Parse and format the data
    const stats = {
      platform: 'chess.com',
      username,
      ratings: {
        rapid: rawData.chess_rapid?.last?.rating || null,
        blitz: rawData.chess_blitz?.last?.rating || null,
        bullet: rawData.chess_bullet?.last?.rating || null,
        daily: rawData.chess_daily?.last?.rating || null,
      },
      record: {
        rapid: {
          wins: rawData.chess_rapid?.record?.win || 0,
          losses: rawData.chess_rapid?.record?.loss || 0,
          draws: rawData.chess_rapid?.record?.draw || 0,
        },
        blitz: {
          wins: rawData.chess_blitz?.record?.win || 0,
          losses: rawData.chess_blitz?.record?.loss || 0,
          draws: rawData.chess_blitz?.record?.draw || 0,
        },
        bullet: {
          wins: rawData.chess_bullet?.record?.win || 0,
          losses: rawData.chess_bullet?.record?.loss || 0,
          draws: rawData.chess_bullet?.record?.draw || 0,
        },
      },
    };

    // Cache the result
    await AsyncStorage.setItem(
      cacheKey,
      JSON.stringify({ data: stats, timestamp: Date.now() })
    );

    return stats;
  } catch (error) {
    console.error('Error fetching Chess.com stats:', error);
    return null;
  }
};

/**
 * Fetch Lichess player stats
 * API Docs: https://lichess.org/api
 */
export const fetchLichessStats = async (username) => {
  if (!username) return null;

  const cacheKey = `lichess_${username}`;
  
  try {
    // Check cache first
    const cached = await AsyncStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }

    // Fetch from API
    const response = await fetch(
      `https://lichess.org/api/user/${username}`
    );

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const rawData = await response.json();
    
    // Parse and format the data
    const stats = {
      platform: 'lichess',
      username,
      ratings: {
        rapid: rawData.perfs?.rapid?.rating || null,
        blitz: rawData.perfs?.blitz?.rating || null,
        bullet: rawData.perfs?.bullet?.rating || null,
        classical: rawData.perfs?.classical?.rating || null,
      },
      record: {
        all: {
          wins: rawData.count?.win || 0,
          losses: rawData.count?.loss || 0,
          draws: rawData.count?.draw || 0,
          total: rawData.count?.all || 0,
        },
      },
    };

    // Cache the result
    await AsyncStorage.setItem(
      cacheKey,
      JSON.stringify({ data: stats, timestamp: Date.now() })
    );

    return stats;
  } catch (error) {
    console.error('Error fetching Lichess stats:', error);
    return null;
  }
};

/**
 * Fetch all available chess platform stats for a user
 */
export const fetchAllChessStats = async (chesscomUsername, lichessUsername) => {
  const [chesscomStats, lichessStats] = await Promise.all([
    fetchChesscomStats(chesscomUsername),
    fetchLichessStats(lichessUsername),
  ]);

  return {
    chesscom: chesscomStats,
    lichess: lichessStats,
  };
};

/**
 * Calculate average rating across all platforms and game types
 */
export const calculateAverageRating = (allStats) => {
  const ratings = [];

  if (allStats.chesscom) {
    Object.values(allStats.chesscom.ratings).forEach(rating => {
      if (rating) ratings.push(rating);
    });
  }

  if (allStats.lichess) {
    Object.values(allStats.lichess.ratings).forEach(rating => {
      if (rating) ratings.push(rating);
    });
  }

  if (ratings.length === 0) return null;

  return Math.round(ratings.reduce((a, b) => a + b, 0) / ratings.length);
};

/**
 * Calculate total win rate across all platforms
 */
export const calculateWinRate = (allStats) => {
  let totalWins = 0;
  let totalGames = 0;

  if (allStats.chesscom) {
    Object.values(allStats.chesscom.record).forEach(record => {
      totalWins += record.wins || 0;
      totalGames += (record.wins || 0) + (record.losses || 0) + (record.draws || 0);
    });
  }

  if (allStats.lichess && allStats.lichess.record.all) {
    totalWins += allStats.lichess.record.all.wins;
    totalGames += allStats.lichess.record.all.total;
  }

  if (totalGames === 0) return 0;

  return ((totalWins / totalGames) * 100).toFixed(1);
};

/**
 * Clear cache for a specific username
 */
export const clearCache = async (chesscomUsername, lichessUsername) => {
  try {
    if (chesscomUsername) {
      await AsyncStorage.removeItem(`chesscom_${chesscomUsername}`);
    }
    if (lichessUsername) {
      await AsyncStorage.removeItem(`lichess_${lichessUsername}`);
    }
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

